const RULE_TYPES = new Set(['mention', 'response', 'threshold', 'escalation', 'score-aggregation']);

export const GOVERNED_RULE_TIMEFRAMES = Object.freeze([
  Object.freeze({ value: 'day', label: 'day', singular: 'day', plural: 'days' }),
  Object.freeze({ value: 'week', label: 'week', singular: 'week', plural: 'weeks' }),
  Object.freeze({ value: 'month', label: 'month', singular: 'month', plural: 'months' }),
  Object.freeze({ value: 'term', label: 'term', singular: 'term', plural: 'terms' }),
  Object.freeze({ value: 'semester', label: 'semester', singular: 'semester', plural: 'semesters' }),
  Object.freeze({ value: 'year', label: 'year', singular: 'year', plural: 'years' }),
]);

const SUPPORTED_TIMEFRAMES = new Set(GOVERNED_RULE_TIMEFRAMES.map(({ value }) => value));
const HISTORY_FILTERS = new Set(['all', 'all-students', 'first-offense', '2-plus', '5-plus']);
const RESPONSE_SCOPES = new Set(['same-offense', 'same-category', 'same-lesson', 'any']);
const FIELD_TYPES = Object.freeze({
  binary: 'boolean',
  boolean: 'boolean',
  multiChoice: 'multiChoice',
  number: 'number',
  star: 'number',
  storageRef: 'storageRef',
  string: 'text',
  text: 'text',
});
const OPERATORS_BY_FIELD_TYPE = Object.freeze({
  boolean: new Set(['equals', 'is-set', 'is-empty']),
  multiChoice: new Set(['in', 'is-set', 'is-empty']),
  number: new Set(['lt', 'lte', 'equals', 'gte', 'gt', 'is-set', 'is-empty']),
  storageRef: new Set(['is-set', 'is-empty']),
  text: new Set(['is-set', 'is-empty']),
});
const UNARY_OPERATORS = new Set(['is-set', 'is-empty']);
const NUMERIC_OPERATORS = new Set(['lt', 'lte', 'gte', 'gt']);

function isActiveSchemaItem(item) {
  return Boolean(item) && item.active !== false && item.retired !== true;
}

function ruleTypeOf(rule) {
  return rule?.type === 'response' ? 'mention' : rule?.type;
}

function fail(rule, message) {
  const title = rule?.title || rule?.name || rule?.id || 'Rule';
  throw new Error(`${title}: ${message}`);
}

function assertPositiveInteger(rule, value, label) {
  const number = Number(value);
  if (!Number.isInteger(number) || number < 1) {
    fail(rule, `${label} must be a positive whole number`);
  }
}

function declaredFields(domain) {
  return [domain?.primary, ...(Array.isArray(domain?.followUp) ? domain.followUp : [])]
    .filter((field) => isActiveSchemaItem(field) && typeof field.id === 'string')
    .map((field) => ({ ...field, type: FIELD_TYPES[field.type] || field.type }));
}

function activeActionDefinitions(domain) {
  return (Array.isArray(domain?.actionDefinitions) ? domain.actionDefinitions : [])
    .filter((definition) => definition
      && typeof definition.id === 'string'
      && definition.active !== false
      && definition.retired !== true);
}

function actionDefinitionFor(domain, rule, prefix = 'consequence') {
  const keys = {
    consequence: ['consequenceTypeId', 'consequenceType'],
    count: ['countTypeId', 'countType'],
    monitors: ['monitorsTypeId', 'monitorsType'],
    escalatesTo: ['escalatesToId', 'escalatesTo'],
  }[prefix];
  const explicitId = rule?.[keys[0]];
  const legacyValue = rule?.[keys[1]];
  const definitions = activeActionDefinitions(domain);
  if (explicitId) return definitions.find((definition) => definition.id === explicitId) || null;
  const matches = definitions.filter((definition) => (
    definition.id === legacyValue
    || definition.value === legacyValue
    || definition.label === legacyValue
  ));
  return matches.length === 1 ? matches[0] : null;
}

function conditionValues(condition) {
  if (condition.operator === 'in') {
    return Array.isArray(condition.value)
      ? condition.value
      : (Array.isArray(condition.values) ? condition.values : []);
  }
  if (Object.prototype.hasOwnProperty.call(condition, 'value')
    && !Array.isArray(condition.value)) {
    return [condition.value];
  }
  return Array.isArray(condition.values) ? condition.values : [];
}

function validateCondition(rule, domain) {
  const condition = rule.eventCondition;
  if (condition == null) return;
  if (!condition || typeof condition !== 'object' || Array.isArray(condition)) {
    fail(rule, 'the Event condition is malformed');
  }
  const field = declaredFields(domain).find((candidate) => candidate.id === condition.fieldId);
  if (!field) fail(rule, `Event field “${condition.fieldId || 'unknown'}” no longer exists`);
  const allowedOperators = OPERATORS_BY_FIELD_TYPE[field.type];
  if (!allowedOperators?.has(condition.operator)) {
    fail(rule, `“${condition.operator || 'unknown'}” is not valid for ${field.type} fields`);
  }

  const values = conditionValues(condition);
  if (UNARY_OPERATORS.has(condition.operator)) {
    const hasValue = values.some((value) => value !== null && value !== undefined);
    if (hasValue) fail(rule, `${condition.operator} must not include a comparison value`);
    return;
  }
  if (condition.operator === 'in' && values.length === 0) {
    fail(rule, 'choose at least one Event value');
  }
  if (condition.operator !== 'in' && values.length !== 1) {
    fail(rule, 'the Event condition needs exactly one comparison value');
  }
  if (NUMERIC_OPERATORS.has(condition.operator)
    && (typeof values[0] !== 'number' || !Number.isFinite(values[0]))) {
    fail(rule, 'the Event comparison must be a number');
  }
  if (field.type === 'number'
    && condition.operator === 'equals'
    && (typeof values[0] !== 'number' || !Number.isFinite(values[0]))) {
    fail(rule, 'the Event comparison must be a number');
  }
  if (field.type === 'boolean' && typeof values[0] !== 'boolean') {
    fail(rule, 'the Event comparison must be Yes or No');
  }
  if (field.type === 'multiChoice') {
    const available = new Set((field.options || [])
      .filter(isActiveSchemaItem)
      .map((option) => String(option?.value ?? option)));
    if (values.some((value) => !available.has(String(value)))) {
      fail(rule, 'the Event condition refers to a retired or unknown choice');
    }
  }
}

function validateLegacyMentionTypes(rule, domain) {
  if (rule.mentionTypes == null) return;
  if (!Array.isArray(rule.mentionTypes)) fail(rule, 'Event values must be a list');
  const primary = declaredFields(domain)
    .find((field) => field.id === domain?.primary?.id);
  if (primary?.type !== 'multiChoice') return;
  const available = new Set((primary.options || [])
    .filter(isActiveSchemaItem)
    .map((option) => String(option?.value ?? option)));
  if (rule.mentionTypes.some((value) => !available.has(String(value)))) {
    fail(rule, 'the Event rule refers to a retired or unknown choice');
  }
}

function declaredRuleMechanisms(domain) {
  const declaration = domain?.capabilities?.rules || {};
  return new Set((declaration.mechanisms || declaration.categories || [])
    .map((mechanism) => mechanism === 'response' ? 'mention' : mechanism));
}

function validateScoreRule(rule, domain) {
  if (domain?.capabilities?.actions?.enabled !== true
    || !domain.capabilities.actions.outcomes?.includes('follow-up')) {
    fail(rule, 'Score rules require the existing Queue follow-up outcome');
  }
  if (rule.childDomain != null && !['mentions', 'recognitions'].includes(rule.childDomain)) {
    fail(rule, 'Choose a supported score follow-up classification');
  }
  if ((rule.consequenceTypeId || rule.consequenceType) && !actionDefinitionFor(domain, rule)) {
    fail(rule, 'the Queue follow-up is missing, retired, or unavailable');
  }
  const conditions = rule.shape === 'compound' ? rule.conditions : [rule];
  if (!Array.isArray(conditions) || !conditions.length || conditions.length > 20) {
    fail(rule, 'A score rule needs between one and twenty conditions');
  }
  for (const condition of conditions) {
    if (!condition || !/^(avg|count|streak)-(below|above)$/.test(condition.shape)) {
      fail(rule, 'Choose an average, count or streak above or below a score');
    }
    if (typeof condition.threshold !== 'number' || !Number.isFinite(condition.threshold)) {
      fail(rule, 'The score threshold must be a finite number');
    }
    if (!condition.shape.startsWith('avg-')) assertPositiveInteger(rule, condition.count, 'Mark count');
    if (!['academic-term', 'academic-semester', 'academic-year', 'last-N-marks'].includes(condition.timeframe)) {
      fail(rule, 'Choose a supported score timeframe');
    }
    if (condition.timeframe === 'last-N-marks') assertPositiveInteger(rule, condition.timeCount, 'Recent mark count');
    const scope = condition.subjectScope;
    if (scope != null && !['same-subject', 'all-subjects'].includes(scope)
      && !(scope && typeof scope === 'object' && !Array.isArray(scope)
        && Object.keys(scope).every(key => key === 'subjectIds')
        && Array.isArray(scope.subjectIds) && scope.subjectIds.length
        && scope.subjectIds.every(id => typeof id === 'string' && id.trim() && !id.includes('/'))
        && new Set(scope.subjectIds).size === scope.subjectIds.length)) {
      fail(rule, 'Choose a supported subject scope');
    }
    const fieldId = condition.subRubricKey || domain.primary?.id;
    if (!declaredFields(domain).some(field => field.id === fieldId && field.type === 'number')) {
      fail(rule, 'The selected score field is missing, retired, or not numeric');
    }
  }
  return true;
}

export function validateGovernedDomainRule(rule, domain) {
  if (!rule?.active || rule.retired === true) return true;
  const type = ruleTypeOf(rule);
  if (!RULE_TYPES.has(rule?.type)) {
    fail(rule, `unsupported rule family “${rule?.type || 'missing'}”`);
  }
  if (domain?.capabilities?.rules?.enabled !== true) {
    fail(rule, 'Rules are not enabled for this Domain');
  }
  if (!declaredRuleMechanisms(domain).has(type)) {
    fail(rule, `the ${type} rule family is not enabled for this Domain`);
  }
  if (type === 'score-aggregation') return validateScoreRule(rule, domain);
  if (!SUPPORTED_TIMEFRAMES.has(rule.timeframe)) {
    fail(rule, `unsupported timeframe “${rule.timeframe || 'missing'}”`);
  }
  assertPositiveInteger(rule, rule.timeCount, 'Time period');
  if (type === 'mention' || type === 'threshold') {
    const quantity = rule.consequenceCount === undefined ? 1 : rule.consequenceCount;
    assertPositiveInteger(rule, quantity, 'Action quantity');
    if (typeof quantity === 'boolean' || Number(quantity) > 999) {
      fail(rule, 'Action quantity must be a whole number from 1 to 999');
    }
  }
  if (rule.groups != null && !Array.isArray(rule.groups)) fail(rule, 'Groups must be a list');
  if (rule.historyFilter != null && !HISTORY_FILTERS.has(rule.historyFilter)) {
    fail(rule, `unsupported history filter “${rule.historyFilter}”`);
  }

  if (type === 'mention') {
    assertPositiveInteger(rule, rule.count, 'Event count');
    if (rule.mentionScope != null && !RESPONSE_SCOPES.has(rule.mentionScope)) {
      fail(rule, `unsupported Event scope “${rule.mentionScope}”`);
    }
    validateCondition(rule, domain);
    validateLegacyMentionTypes(rule, domain);
    if (!actionDefinitionFor(domain, rule)) {
      fail(rule, 'the Queue follow-up is missing, retired, or unavailable');
    }
  }

  if (type === 'threshold') {
    assertPositiveInteger(rule, rule.count, 'Action count');
    if (!actionDefinitionFor(domain, rule, 'count')) {
      fail(rule, 'the counted Action is missing, retired, or unavailable');
    }
    if (!actionDefinitionFor(domain, rule)) {
      fail(rule, 'the resulting Queue follow-up is missing, retired, or unavailable');
    }
  }

  if (type === 'escalation') {
    assertPositiveInteger(rule, rule.threshold, 'Escalation count');
    const monitored = actionDefinitionFor(domain, rule, 'monitors');
    const target = actionDefinitionFor(domain, rule, 'escalatesTo');
    if (!monitored) fail(rule, 'the monitored Action is missing, retired, or unavailable');
    if (!target) fail(rule, 'the replacement Action is missing, retired, or unavailable');
    if (monitored.id === target.id) fail(rule, 'an escalation cannot replace an Action with itself');
    if (rule.resetAfterUse != null && typeof rule.resetAfterUse !== 'boolean') {
      fail(rule, 'Reset after use must be Yes or No');
    }
  }
  return true;
}

/**
 * Return a display-safe diagnostic for a persisted active rule.
 *
 * Runtime validation remains fail-closed and throws. The editor needs the same
 * authority in a non-throwing form so a rule invalidated by a later Domain
 * configuration change remains visible to administrators instead of silently
 * disappearing from the operational picture.
 */
export function getGovernedDomainRuleIssue(rule, domain) {
  if (!rule?.active || rule.retired === true) return null;

  try {
    validateGovernedDomainRule(rule, domain);
    return null;
  } catch (error) {
    return {
      ruleId: rule.id || null,
      ruleType: ruleTypeOf(rule) || 'unknown',
      title: rule.title || rule.name || 'Untitled rule',
      message: error instanceof Error ? error.message : String(error),
      rule,
    };
  }
}

function findEscalationCycles(rules, domain) {
  const edges = new Map();
  const allEdges = [];
  for (const rule of rules) {
    if (!rule?.active || rule.retired === true || ruleTypeOf(rule) !== 'escalation') continue;
    const from = actionDefinitionFor(domain, rule, 'monitors')?.id;
    const to = actionDefinitionFor(domain, rule, 'escalatesTo')?.id;
    if (!from || !to) continue;
    if (!edges.has(from)) edges.set(from, []);
    const edge = { from, to, rule };
    edges.get(from).push(edge);
    allEdges.push(edge);
  }

  let nextIndex = 0;
  const indexes = new Map();
  const lowLinks = new Map();
  const stack = [];
  const onStack = new Set();
  const cycles = [];

  // Strongly connected components identify every rule participating in a
  // cycle, not merely the first DFS back-edge. That matters when multiple
  // escalation paths share an Action.
  const visit = (node) => {
    indexes.set(node, nextIndex);
    lowLinks.set(node, nextIndex);
    nextIndex += 1;
    stack.push(node);
    onStack.add(node);

    for (const { to } of edges.get(node) || []) {
      if (!indexes.has(to)) {
        visit(to);
        lowLinks.set(node, Math.min(lowLinks.get(node), lowLinks.get(to)));
      } else if (onStack.has(to)) {
        lowLinks.set(node, Math.min(lowLinks.get(node), indexes.get(to)));
      }
    }

    if (lowLinks.get(node) !== indexes.get(node)) return;
    const component = [];
    let member;
    do {
      member = stack.pop();
      onStack.delete(member);
      component.push(member);
    } while (member !== node);

    const componentSet = new Set(component);
    const cycleEdges = allEdges.filter(({ from, to }) => (
      componentSet.has(from) && componentSet.has(to)
    ));
    const isCycle = component.length > 1
      || cycleEdges.some(({ from, to }) => from === to);
    if (!isCycle) return;

    const labels = [...component, component[0]].join(' → ');
    cycles.push({
      rules: [...new Set(cycleEdges.map(({ rule }) => rule))],
      message: `Escalation rules form a cycle (${labels}). Change one replacement Action before activating.`,
    });
  };

  const nodes = new Set(allEdges.flatMap(({ from, to }) => [from, to]));
  for (const node of nodes) {
    if (!indexes.has(node)) visit(node);
  }
  return cycles;
}

function assertNoEscalationCycle(rules, domain) {
  const [cycle] = findEscalationCycles(rules, domain);
  if (cycle) throw new Error(cycle.message);
}

/**
 * Diagnose persisted active rules against both their own schema and the full
 * active graph. Save-time validation uses the same set contract, while this
 * non-throwing form keeps pre-existing conflicts visible and repairable.
 */
export function getGovernedDomainRuleSetIssues(rules, domain) {
  const activeRules = (Array.isArray(rules) ? rules : [])
    .filter((rule) => rule?.active === true && rule.retired !== true);
  const individualIssues = new Map(activeRules
    .map((rule) => [rule, getGovernedDomainRuleIssue(rule, domain)])
    .filter(([, issue]) => Boolean(issue)));
  const cycleMessages = new Map();

  findEscalationCycles(activeRules, domain).forEach((cycle) => {
    cycle.rules.forEach((rule) => {
      if (!cycleMessages.has(rule)) cycleMessages.set(rule, cycle.message);
    });
  });

  return activeRules.flatMap((rule) => {
    const individualIssue = individualIssues.get(rule);
    const cycleMessage = cycleMessages.get(rule);
    if (individualIssue && cycleMessage) {
      return [{
        ...individualIssue,
        message: `${individualIssue.message}; ${cycleMessage}`,
      }];
    }
    if (individualIssue) return [individualIssue];
    if (!cycleMessage) return [];
    const title = rule.title || rule.name || 'Untitled rule';
    return [{
      ruleId: rule.id || null,
      ruleType: ruleTypeOf(rule) || 'unknown',
      title,
      message: `${title}: ${cycleMessage}`,
      rule,
    }];
  });
}

export function validateGovernedDomainRuleSet(rules, domain) {
  const activeRules = (Array.isArray(rules) ? rules : [])
    .filter((rule) => rule?.active === true && rule.retired !== true);
  activeRules.forEach((rule) => validateGovernedDomainRule(rule, domain));
  assertNoEscalationCycle(activeRules, domain);
  return true;
}
